/**
 * Hook that calls the `saveContractExtraction` mutation (SE-4385) to
 * persist a reviewed contract extraction, transition its status to
 * `SAVED`, and delete the source documents from S3.
 *
 * Refetches the `GetContractExtraction` query on success so the drawer
 * re-renders in read-only mode as soon as the server confirms the
 * save. S3 cleanup failures on the backend are logged but do not fail
 * the mutation (Story 2.5 technical notes — lifecycle policy is the
 * safety net), so a `true` return reliably indicates the DB commit
 * succeeded.
 *
 * SE-4819: instead of refetching the entire `playerContractsOverview`
 * query on success, the mutation `update` callback writes the SAVED
 * status delta directly into the cached Active-list entry. The prior
 * full refetch was occasionally not picked up by the live observer
 * (the user had to manually refresh the page to see the green
 * checkmark); a targeted cache write is reactive and cheaper.
 *
 * The GraphQL document is defined inline via `gql` (same convention
 * used by the other extraction hooks in this feature) so it can ship
 * before the codegen step runs against the updated schema.
 * @module features/contracts/hooks/useSaveContractExtraction
 */

import { gql, useMutation } from "@apollo/client";
import * as Sentry from "@sentry/react-native";
import { useCallback, useMemo, useState } from "react";

import type { ExtractionStatus } from "../types";

import { applyContractExtractionSaveToCache } from "./applyContractExtractionSaveToCache";
import { activeContractExtractionRefetch } from "./contractExtractionRefetch";

/** GraphQL document for the SaveContractExtraction mutation. */
export const SAVE_CONTRACT_EXTRACTION_MUTATION = gql`
  mutation SaveContractExtraction($extractionId: ID!) {
    saveContractExtraction(extractionId: $extractionId) {
      id
      status
      savedAt
    }
  }
`;

/** Shape of the SaveContractExtraction mutation response. */
interface SaveContractExtractionResponse {
  readonly saveContractExtraction: {
    readonly id: string;
    readonly status: ExtractionStatus;
    readonly savedAt: string | null;
  };
}

/** Shape of the SaveContractExtraction mutation variables. */
interface SaveContractExtractionVariables {
  readonly extractionId: string;
}

/** Public API returned by `useSaveContractExtraction`. */
export interface UseSaveContractExtractionResult {
  /**
   * Calls the mutation to save the extraction. Returns `true` when the
   * server confirms the status transitioned to `SAVED`, `false` on any
   * error. Refetches the `GetContractExtraction` query on success so
   * the drawer re-renders in read-only mode against the updated state.
   * @param extractionId - UUID of the extraction to save
   * @param playerId - Player UUID (needed for the extraction query refetch variables)
   * @param documentType - Document type (needed for the extraction query refetch variables)
   */
  readonly saveExtraction: (
    extractionId: string,
    playerId: string,
    documentType: string
  ) => Promise<boolean>;
  /** Whether a save request is currently in flight. */
  readonly isSaving: boolean;
}

/**
 * Wraps the SaveContractExtraction mutation with loading state and an
 * automatic refetch of the extraction query on success.
 * @returns Imperative save function + loading state
 */
export function useSaveContractExtraction(): UseSaveContractExtractionResult {
  const [runMutation] = useMutation<
    SaveContractExtractionResponse,
    SaveContractExtractionVariables
  >(SAVE_CONTRACT_EXTRACTION_MUTATION);

  const [isSaving, setIsSaving] = useState(false);

  const saveExtraction = useCallback(
    async (
      extractionId: string,
      playerId: string,
      documentType: string
    ): Promise<boolean> => {
      setIsSaving(true);
      try {
        const { data } = await runMutation({
          variables: { extractionId },
          // The drawer's per-player extraction observer needs a fresh
          // network read to flip into read-only mode against the new
          // server state. The contracts overview is updated via the
          // narrower `update` callback below — see SE-4819.
          refetchQueries: [
            activeContractExtractionRefetch(playerId, documentType),
          ],
          awaitRefetchQueries: true,
          // SE-4819: write the SAVED delta straight into the cached
          // `playerContractsOverview(archivedOnly: false)` query so the
          // matching row's status cell flips to the green checkmark
          // synchronously. Replaces a full refetch that was sometimes
          // not picked up by the live observer.
          update: (cache, result) => {
            if (result.data?.saveContractExtraction?.status !== "SAVED") return;
            applyContractExtractionSaveToCache(cache, {
              playerId,
              documentType,
              savedAt: result.data.saveContractExtraction.savedAt,
            });
          },
        });
        const status = data?.saveContractExtraction?.status;
        if (status === "SAVED") return true;

        Sentry.captureMessage(
          "saveContractExtraction returned non-SAVED status",
          {
            level: "error",
            extra: { extractionId, playerId, documentType, status },
          }
        );
        return false;
      } catch (err) {
        Sentry.captureException(err, {
          extra: { extractionId, playerId, documentType },
        });
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [runMutation]
  );

  return useMemo(
    () => ({ saveExtraction, isSaving }),
    [saveExtraction, isSaving]
  );
}
